// @flow
import { WarningCircle } from '@phosphor-icons/react'
import * as React from 'react'
import type { Question } from 'types/question'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Space from 'ui/atoms/Space'
import Text from 'ui/Text'
import { CONSTANTS_FORM_BUILDER } from 'utils/FormBuilder/ConstantsFormBuilder'
import { hasText } from 'utils/helpers'

import { QuestionInfo } from '@/components/QuestionInfo'
import cn from '@/utils/tailwind/cn'

import { FormBuilderContext } from '../../Contexts/FormBuilderContext'
import { BuilderQuestionFactory } from './BuilderQuestionFactory'
import BuilderSelectableItem from './BuilderSelectableItem'

type Props = {
  addRowToTableInBuilder: Function,
  errors: Array<string>,
  hasMakeBiggerOption: boolean,
  isDragDisabled: boolean,
  provided?: ?{
    innerRef: React.ElementRef<any>,
    draggableProps: any,
    dragHandleProps: any,
  },
  question: Question,
  questionRef: React.ElementRef<any>,
}

function BuilderQuestionSelectableItem({
  addRowToTableInBuilder,
  errors,
  hasMakeBiggerOption,
  isDragDisabled,
  provided,
  question,
  questionRef,
}: Props): React.Node {
  const questionId = question.id.toString()
  const formBuilderContext = React.useContext(FormBuilderContext)

  const hasMakeSmallerOption = (question?.display_columns ?? 6) > 1

  const showHelp =
    hasText(question.help_text) &&
    !['matrix', 'table'].includes(question.question_type.question_type)

  const isInRepeatableSection = React.useMemo(() => {
    if (!formBuilderContext?.getSection || !formBuilderContext?.getPage) {
      return false
    }

    const questionSection = formBuilderContext.getSection(question.section)
    if (!questionSection) return false

    const questionPage = formBuilderContext.getPage(questionSection.page)
    return questionPage?.repeat_section === true
  }, [formBuilderContext, question.section])

  const isMatrixOrTable = ['matrix', 'table'].includes(
    question.question_type.question_type
  )

  const showRepeatableWarning = isInRepeatableSection && isMatrixOrTable

  return (
    <div
      className={`px-1 col-span-${question?.display_columns ?? 6}`}
      ref={provided ? provided.innerRef : null}
      {...(provided?.draggableProps || {})}
      {...(provided?.dragHandleProps || {})}
    >
      <BuilderSelectableItem
        hasErrors={errors.length >= 1}
        hasMakeBiggerOption={hasMakeBiggerOption}
        hasMakeSmallerOption={hasMakeSmallerOption}
        id={questionId}
        type={CONSTANTS_FORM_BUILDER.SELECTION_TYPE.QUESTION}
      >
        <div
          className={cn('relative px-2 py-2', {
            'pointer-events-none': !isDragDisabled,
          })}
          ref={questionRef}
        >
          <Space>
            <div>
              <span
                className={cn({
                  'opacity-50': showRepeatableWarning,
                })}
              >
                <BuilderQuestionFactory
                  addRowToTableInBuilder={addRowToTableInBuilder}
                  question={question}
                />
              </span>
              {showHelp && (
                <Text.HelperText>{question.help_text}</Text.HelperText>
              )}
              {errors &&
                errors.map((error, index) => (
                  <p key={index}>
                    <ErrorLabel>{error}</ErrorLabel>
                  </p>
                ))}
              {showRepeatableWarning && (
                <QuestionInfo className="mt-2">
                  <WarningCircle className="pr-1 text-xl" weight="fill" />
                  This field will not appear in repeatable sections.
                </QuestionInfo>
              )}
            </div>

            {question.hidden ? (
              <QuestionInfo className="mt-2">
                <WarningCircle className="pr-1 text-xl" weight="fill" /> This
                field will not be seen on the form.
              </QuestionInfo>
            ) : null}
          </Space>
        </div>
      </BuilderSelectableItem>
    </div>
  )
}

export default BuilderQuestionSelectableItem
